import { useCallback, useEffect, useState } from 'react';
import {
  getSavedCredentials,
  mergeSavedCredentials,
  persistCredentialsPreference,
} from './savedCredentials';

/**
 * Shared hook for opt-in credential persistence across login forms.
 *
 * - Prefills from sessionStorage only when the user previously consented
 * - Tracks the "remember for later" checkbox state
 * - Exposes persistCredentials() to call on submit
 *
 * @param {object} [options]
 * @param {(updater: object|Function) => void} [options.setInputs] - optional inputs setter to prefill
 * @param {boolean} [options.enabled=true] - set false to skip auto-prefill
 */
const useRememberCredentials = ({ setInputs, enabled = true } = {}) => {
  const [rememberCredentials, setRememberCredentials] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const saved = getSavedCredentials();
    setRememberCredentials(Boolean(saved.consent));

    if (!setInputs || !saved.consent) return;

    setInputs((prev) => mergeSavedCredentials(prev || {}));
    // Prefill only on mount; setInputs is expected to be a stable useState setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const persistCredentials = useCallback(
    ({ identification = '', phone = '' } = {}) => {
      persistCredentialsPreference({
        remember: rememberCredentials,
        identification,
        phone,
      });
    },
    [rememberCredentials]
  );

  return {
    rememberCredentials,
    setRememberCredentials,
    persistCredentials,
  };
};

export default useRememberCredentials;
